const SESSION_KEY = "oxiclear_atlas_chat_token_v1";
const POLL_INTERVAL_MS = 15_000;

class OxiClearAtlasChat extends HTMLElement {
  constructor() {
    super();
    this.token = "";
    this.pollTimer = 0;
    this.busy = false;
    this.messages = [];
  }

  connectedCallback() {
    this.endpoint = this.dataset.endpoint || "";
    this.pagePath = this.dataset.pagePath || window.location.pathname || "/";
    this.launcher = this.querySelector("[data-atlas-launcher]");
    this.panel = this.querySelector("[data-atlas-panel]");
    this.closeButton = this.querySelector("[data-atlas-close]");
    this.messageList = this.querySelector("[data-atlas-messages]");
    this.status = this.querySelector("[data-atlas-status]");
    this.form = this.querySelector("[data-atlas-form]");
    this.input = this.querySelector("[data-atlas-input]");
    this.sendButton = this.querySelector("[data-atlas-send]");
    this.website = this.querySelector("[data-atlas-website]");

    if (!this.endpoint || !this.launcher || !this.panel || !this.form || !this.input) {
      return;
    }

    this.token = this.readStoredToken();
    this.launcher.addEventListener("click", () => this.open());
    this.closeButton?.addEventListener("click", () => this.close());
    this.form.addEventListener("submit", (event) => this.send(event));
    this.input.addEventListener("input", () => this.resizeComposer());
    this.input.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        this.form.requestSubmit();
      }
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !this.panel.hidden) {
        this.close();
      }
    });
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden && !this.panel.hidden && this.token) {
        void this.refreshHistory();
      }
    });

    this.renderMessages();
  }

  disconnectedCallback() {
    this.stopPolling();
  }

  readStoredToken() {
    try {
      return window.sessionStorage.getItem(SESSION_KEY) || "";
    } catch {
      return "";
    }
  }

  storeToken(token) {
    this.token = token;

    try {
      window.sessionStorage.setItem(SESSION_KEY, token);
    } catch {
      // The current tab can continue even when storage is unavailable.
    }
  }

  forgetToken() {
    this.token = "";

    try {
      window.sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // Nothing else is required when storage is unavailable.
    }
  }

  async open() {
    this.panel.hidden = false;
    this.launcher.setAttribute("aria-expanded", "true");
    this.launcher.hidden = true;
    this.input.focus({ preventScroll: true });

    if (this.token) {
      await this.refreshHistory();
    } else {
      await this.startSession();
    }

    this.startPolling();
  }

  close() {
    this.panel.hidden = true;
    this.launcher.hidden = false;
    this.launcher.setAttribute("aria-expanded", "false");
    this.stopPolling();
    this.launcher.focus({ preventScroll: true });
  }

  startPolling() {
    this.stopPolling();
    this.pollTimer = window.setInterval(() => {
      if (!document.hidden && !this.busy && this.token) {
        void this.refreshHistory({ quiet: true });
      }
    }, POLL_INTERVAL_MS);
  }

  stopPolling() {
    if (this.pollTimer) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = 0;
    }
  }

  async request(action, payload = {}) {
    const headers = { "Content-Type": "application/json" };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(this.endpoint, {
      method: "POST",
      mode: "cors",
      cache: "no-store",
      headers,
      body: JSON.stringify({
        action,
        pagePath: window.location.pathname || this.pagePath,
        website: this.website?.value || "",
        ...payload,
      }),
    });
    const result = await response.json().catch(() => ({}));

    if (!response.ok || !result.success) {
      const error = new Error(result.error || "Atlas chat is temporarily unavailable.");
      error.status = response.status;
      throw error;
    }

    return result.data;
  }

  async startSession() {
    this.setBusy(true, "Connecting to Atlas…");

    try {
      const data = await this.request("start");
      this.storeToken(data.token);
      this.messages = Array.isArray(data.messages) ? data.messages : [];
      this.renderMessages();
      this.setStatus("");
    } catch (error) {
      this.setStatus(error.message, "error");
    } finally {
      this.setBusy(false);
    }
  }

  async refreshHistory(options = {}) {
    if (!this.token) {
      return;
    }

    try {
      const data = await this.request("history");
      this.messages = Array.isArray(data.messages) ? data.messages : [];
      this.renderMessages();

      if (!options.quiet) {
        this.setStatus("");
      }
    } catch (error) {
      if (error.status === 401) {
        this.forgetToken();
        await this.startSession();
        return;
      }

      if (!options.quiet) {
        this.setStatus(error.message, "error");
      }
    }
  }

  async send(event) {
    event.preventDefault();

    const message = this.input.value.trim();

    if (message.length < 2 || this.busy) {
      return;
    }

    if (!this.token) {
      await this.startSession();
    }

    if (!this.token) {
      return;
    }

    const optimisticId = `visitor-${Date.now()}`;
    this.messages = [
      ...this.messages,
      {
        id: optimisticId,
        sender: "visitor",
        body: message,
        createdAt: new Date().toISOString(),
      },
    ];
    this.input.value = "";
    this.resizeComposer();
    this.renderMessages({ pending: true });
    this.setBusy(true, "Atlas is checking…");

    try {
      const data = await this.request("message", { message });
      this.messages = Array.isArray(data.messages) ? data.messages : [];
      this.renderMessages();
      this.setStatus("");
    } catch (error) {
      this.messages = this.messages.filter((item) => item.id !== optimisticId);
      this.input.value = message;
      this.resizeComposer();
      this.renderMessages();

      if (error.status === 401) {
        this.forgetToken();
      }

      this.setStatus(error.message, "error");
    } finally {
      this.setBusy(false);
      this.input.focus({ preventScroll: true });
    }
  }

  renderMessages(options = {}) {
    if (!this.messageList) {
      return;
    }

    this.messageList.replaceChildren();

    if (this.messages.length === 0) {
      this.messageList.append(
        this.createMessage({
          sender: "atlas",
          body: "Hi, I’m Atlas from OxiClear. How can I help?",
        }),
      );
    } else {
      this.messages.forEach((message) => {
        this.messageList.append(this.createMessage(message));
      });
    }

    if (options.pending) {
      const typing = document.createElement("div");
      typing.className = "oc-atlas-chat__typing";
      typing.textContent = "Atlas is checking";
      this.messageList.append(typing);
    }

    window.requestAnimationFrame(() => {
      this.messageList.scrollTop = this.messageList.scrollHeight;
    });
  }

  createMessage(message) {
    const sender = ["visitor", "atlas", "support"].includes(message.sender)
      ? message.sender
      : "support";
    const labels = {
      visitor: "You",
      atlas: "Atlas",
      support: "OxiClear Support",
    };
    const item = document.createElement("article");
    const label = document.createElement("span");
    const body = document.createElement("p");

    item.className = `oc-atlas-chat__message oc-atlas-chat__message--${sender}`;
    label.className = "oc-atlas-chat__message-label";
    label.textContent = labels[sender];
    body.className = "oc-atlas-chat__message-body";
    this.appendMessageBody(body, String(message.body || ""));
    item.append(label, body);

    return item;
  }

  appendMessageBody(container, value) {
    const guideUrl = "https://www.oxiclear.com/pages/how-to-use";
    const parts = value.split(guideUrl);

    parts.forEach((part, index) => {
      container.append(document.createTextNode(part));

      if (index < parts.length - 1) {
        const link = document.createElement("a");
        link.href = guideUrl;
        link.target = "_blank";
        link.rel = "noopener";
        link.textContent = "Open the How to Use guide";
        container.append(link);
      }
    });
  }

  setBusy(busy, message = "") {
    this.busy = busy;
    this.input.disabled = busy;
    this.sendButton.disabled = busy;

    if (message) {
      this.setStatus(message);
    }
  }

  setStatus(message, tone = "") {
    this.status.textContent = message || "";

    if (tone) {
      this.status.dataset.tone = tone;
    } else {
      delete this.status.dataset.tone;
    }
  }

  resizeComposer() {
    this.input.style.height = "auto";
    this.input.style.height = `${Math.min(this.input.scrollHeight, 112)}px`;
  }
}

if (!customElements.get("oxiclear-atlas-chat")) {
  customElements.define("oxiclear-atlas-chat", OxiClearAtlasChat);
}
